import styled from "@emotion/styled";
import { makeStyles } from "@material-ui/core/styles";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { toast } from "react-toastify";
import { resolveBarcode } from "../../store/barcodeStore/barcodeThunk";
import { Autocomplete, Box, Button, Chip, Divider, Drawer, Grid, IconButton, InputAdornment, Skeleton, TableCell, TableRow, TextField, Tooltip, Typography, tableCellClasses } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchAccountSkuByName, fetchInventories, fetchItemCategories } from '../../store/inventoryStore/inventoryThunk';
import { fetchLocations } from "../../store/locationStore/locationThunk";
import { fetchLocationBatches } from "../../store/stockStore/stockThunk";
import { Utils } from "../../utils/common-utils";
import { IOptions } from "../FormFields/CustomSelect";
import AddItem from "./AddItem";
import InputNumber from "./NumberInput";
import DefaultImg from './product.png';


export interface InventoryLineItem {
  id: number;
  seqNumber: number;
  skuCode?: string;
  _id?: string;
  name: string;
  description: string;
  mrp: string;
  appliedRate: number;
  unit: number;
  qty: number;
  discount: number;
  discountPercentage: number;
  discountAmount: number;
  taxableValue: number;
  tax: number;
  total: number;

}


// Right-side drawer paper. The inner content still uses absolute positioning
// (top: 50 header + two absolutely-positioned panels), so this Box provides the
// positioned, full-height context the drawer paper fills.
const style = {
  position: 'relative' as 'relative',
  width: '90vw',
  height: '100vh',
  bgcolor: 'background.paper',
  border: 'none',
  overflow: 'hidden',
};

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    width: '108%',
    boxShadow: 'none',
    marginLeft: '-6px',
    borderRadius: 7
  }
});

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 3,
  width: 15,
  height: 15,
  boxShadow:
    //   theme.palette.mode === 'dark'
    // ? '0 0 0 1px rgb(16 22 26 / 40%) ' :
    'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor:
    // theme.palette.mode === 'dark' ? '#394b59' : 
    '#f5f8fa',
  backgroundImage:
    //   theme.palette.mode === 'dark'
    // ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))' :
    'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor:
      //   theme.palette.mode === 'dark' ? '#30404d' : 
      '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background:
      // theme.palette.mode === 'dark' ? 'rgba(57,75,89,.5)' : 
      'rgba(206,217,224,.5)',
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&:before': {
    display: 'block',
    width: 15,
    height: 15,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#9da7b7',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    margin: 0,
    padding: 0,
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    left: 0,
    bottom: 0, // <-- KEY
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: 14,
    padding: 5,
    border: 'none',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#fbfbfb',
    // border: '0.5px dashed #9da7b7',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: '#e9ecf1',
    // border: '0.5px dashed #9da7b7',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
  },
}));

const ItemModel = (props: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const classes = useStyles();
  const TIME_DELAY = 500;

  const { inventories, status } = useAppSelector(state => state.inventories);
  const { permissions, selectedAccount } = useAppSelector(state => state.auth);
  const { locations } = useAppSelector(state => state.locations);
  const [items, setItems] = useState<any[]>([]);
  const [suggestedItems, setSuggestedItems] = useState<any[]>([]);
  const [newCartItemId, setNewCartItemId] = useState("");
  const [isNewCart, setIsNewCart] = useState("");
  const [cart, setCart] = useState<any[]>([]);
  const [addItem, setAddItem] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [reloadItems, setReloadItems] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Variant picker: the parent item card whose variants are being chosen. When
  // set, a panel slides out from the border between the item list & the cart.
  const [variantParent, setVariantParent] = useState<any>(null);

  // Infinite-scroll pagination for the item grid.
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const fetchingRef = useRef(false);

  // Filters States
  const [itemCategoryOptions, setItemCategoryOptions] = useState<IOptions[]>([]);
  const [itemCategoryId, setItemCategoryId] = useState("");
  const [itemCategoryValue, setItemCategoryValue] = useState(null);
  const [filterItemName, setFilterItemName] = useState("");

  // Location + (location-dependent) Batch filters. When a location is selected,
  // each card's stock is re-aggregated to its on-hand at that location (and batch).
  const [locationId, setLocationId] = useState("");
  const [locationValue, setLocationValue] = useState<any>(null);
  const [batchId, setBatchId] = useState("");
  const [batchValue, setBatchValue] = useState<any>(null);
  const [batchOptions, setBatchOptions] = useState<any[]>([]);

  // Barcode scan-to-cart.
  const [scanCode, setScanCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const scanningRef = useRef(false); // synchronous guard against a double Enter
  // Ignore the same code fired again within this window (key auto-repeat / scanner
  // CR+LF): a fast 404 resets scanningRef before the 2nd Enter, so a time guard is needed.
  const lastScanRef = useRef<{ code: string; at: number }>({ code: "", at: 0 });

  const onClose = () => {
    props.setVisible(false);
    // NOTE: do NOT setItems(inventories) here — `inventories` is the raw redux
    // list without the display fields (inStock, unit, appliedRate, expiry,
    // cardBatchNumber…) that prepareLineItems() adds. Doing so left the prepared
    // grid replaced by broken cards on reopen. The open-effect below refetches a
    // fresh prepared page instead.
    setCart([]);
    setCheckAll(false);
    setTotalAmount(0);
    setVariantParent(null);
  }

  /**
  * Fetch all the accounts on Accounts page load.
  */

  useEffect(() => {
    let isMounted = true;
    const getItemCategories = async () => {
      let options: IOptions[] = [];
      const data: any = await dispatch(fetchItemCategories());
      if (data.payload.length > 0) {
        await data.payload.forEach(async (item: string | any) => {
          options.push({ label: item.name, value: item._id })
        });
        if (isMounted && options.length > 0) {
          setItemCategoryOptions(options);
        }
      }
    }
    getItemCategories();
    return () => {
      isMounted = false;
    };

  }, []);

  useEffect(() => {
    console.log("useEffect called 1111 fetchInventories =========>");
    const { _id: id } = selectedAccount;
    dispatch(fetchInventories({ accountId: id, type: '', params: '' }));
  }, [dispatch, selectedAccount]);

  // Location options for the filter.
  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  const locationOptions = useMemo(
    () => (locations || []).map((l: any) => ({ value: l._id, label: `${l.name} (${l.code})` })),
    [locations]
  );

  // Preselect the account's default location. The list is already access-filtered,
  // so a user without rights to the default falls back to their first permitted one.
  const appliedStartLocation = useRef(false);
  useEffect(() => {
    if (appliedStartLocation.current || locationId || !locations || locations.length === 0) return;
    const usable = locations.filter((l: any) => l.isActive !== false);
    const pick = usable.find((l: any) => l.isDefault) || usable[0] || locations[0];
    if (!pick) return;
    appliedStartLocation.current = true;
    setLocationValue({ value: pick._id, label: `${pick.name} (${pick.code})` });
    setLocationId(pick._id);
  }, [locations, locationId]);

  // When the location changes, load the batches that have stock there (dependent
  // Batch filter). No location -> no batches, no on-hand override.
  useEffect(() => {
    if (!locationId) {
      setBatchOptions([]);
      return;
    }
    let active = true;
    dispatch(fetchLocationBatches(locationId))
      .unwrap()
      .then((data: any) => {
        if (active) setBatchOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setBatchOptions([]);
      });
    return () => {
      active = false;
    };
  }, [dispatch, locationId]);

  const onChangeLocation = (val: any) => {
    setLocationValue(val);
    setLocationId(val?.value || "");
    setPage(0);
    // changing location invalidates the chosen batch
    setBatchId("");
    setBatchValue(null);
  };

  const onChangeBatch = (val: any) => {
    setBatchValue(val);
    setBatchId(val?.value || "");
    setPage(0);
  };

  // The server fully controls filtering AND ordering (latest expiry first when a
  // location is filtered), so the grid renders the list as-is.
  const displayedItems = items;

  const batchOptionList = useMemo(
    () =>
      batchOptions.map((b: any) => ({
        value: b._id,
        label: `${b.batchNumber || "—"}${b.expiryDate ? " · Exp " + Utils.formatDateLong(b.expiryDate) : ""}`,
      })),
    [batchOptions]
  );

  useEffect(() => {
    console.log("useEffect called 222");
    console.log(inventories);
    if (inventories.length > 0) {
      const updatelist = prepareLineItems(inventories);
      // var i = 0
      // const updatelist = inventories.map((c:any) => {
      //     i = i + 1
      //     return {...c,
      //       id: i,
      //       appliedRate: c.sale.price,
      //       purchasePrice: c.purchase ? c.purchase.price : '-',
      //       taxPercentage: c.gst ? Number(c.gst.value) : 0,
      //       wholeSalePrice: c.wholeSale ? Number(c.wholeSale.price) : '-',
      //       inStock: c.stock.currentStock,
      //       unit: c.sale.unit.code,
      //       discount: (c.discount && c.discount.discountAmount) ? c.discount.discountAmount : 0,
      //       discountPercentage: (c.discount && c.discount.discountPercent) ? c.discount.discountPercent : 0,
      //       checked: false,
      //       qty:0,
      //       igst: 0,
      //       igsttotal: 0,
      //       cgst: 0,
      //       cgsttotal: 0,
      //       sgst: 0,
      //       sgsttotal: 0,
      //       total: 0,
      //       _id: '',
      //       skuCode: c._id != '' ? c._id : ''
      //     };
      // });
      setItems(updatelist);
      // setNewCartItemId('');
    }
  }, [inventories, reloadItems]);

  useEffect(() => {
    searchItems('');
    setNewCartItemId('');
  }, [])

  // ItemModel stays mounted for the whole invoice form (the parent only toggles
  // the drawer's `open`), so reopening doesn't remount or refetch on its own.
  // Force a fresh, prepared page load with the current filters each time the
  // drawer opens so the grid is never left stale/broken between opens.
  useEffect(() => {
    if (props.visible) {
      setNewCartItemId('');
      setPage(0);
      setReloadItems(Math.random().toString(36).substring(2, 7));
    }
  }, [props.visible])

  useEffect(() => {
    TotalAmount();
    if (cart.find(e => e.checked === false)) {
      setCheckAll(false);
    } else {
      setCheckAll(true);
    }
  }, [cart])


  const onSubmit = () => {
    if (cart && cart.length > 0) {
      let data: any = [];
      cart.map((c) => {
        if (c.checked === true) {
          data.push(c);
        }
      });
      props.itemData(data);
      // Pass the selected location back to the invoice form so the invoice
      // is saved against the correct branch (e.g., Mirakel Kids).
      if (props.onLocationSelect && locationId) {
        props.onLocationSelect(locationId);
      }
      props.setVisible(false);
      // setItems(inventories);
      setCheckAll(false);
      setTotalAmount(0);
      setCart([]);
    }
  }

  const TotalAmount = () => {
    let num = 0;
    cart.map((o, i) => {
      if (o.checked === true) {
        num = num + ((o.appliedRate && o.appliedRate > 0) ? o.appliedRate : o.mrp) * o.qty;
      }
    })
    setTotalAmount(num);
  }

  const searchItems = async (searchValue: string) => {
    setNewCartItemId('');
    setFilterItemName(searchValue);
    setPage(0);
  }

  useEffect(() => {
    setIsLoading(true);
    fetchingRef.current = true;
    let filter = (itemCategoryId && itemCategoryId != '') ? `&category=${itemCategoryId}` : '';
    let locFilter = locationId ? `&locationId=${locationId}` : '';
    let batchFilter = (locationId && batchId) ? `&batchId=${batchId}` : '';
    let queryString = `name=${filterItemName}${filter}${locFilter}${batchFilter}&page=${page}`;
    setNewCartItemId('')

    const timerId = setTimeout(() => {
      dispatch(fetchAccountSkuByName(queryString)).unwrap().then((data: any) => {
        const list = data && data.inventories ? prepareLineItems(data.inventories, page * PAGE_SIZE) : [];
        setItems((prev) => (page === 0 ? list : [...prev, ...list]));
        const total = Number(data && data.total) || 0;
        setHasMore((page + 1) * PAGE_SIZE < total);
        setNewCartItemId('');
        setIsLoading(false);
        fetchingRef.current = false;
      }).catch(() => {
        setIsLoading(false);
        fetchingRef.current = false;
      });
    }, TIME_DELAY);  // 500ms debounce

    // Cancel the pending request when a dependency changes again within the
    // window (e.g. the next keystroke), so search is truly debounced.
    return () => clearTimeout(timerId);



    // dispatch(fetchAccountSkuByName(queryString)).unwrap().then((data: any) => {
    //   if (data) {
    //     if(data.inventories && data.inventories.length > 0) {
    //       const updatelist = prepareLineItems(data.inventories);
    //       // setCart([...cart, updatelist]);
    //       setItems(updatelist);
    //       setNewCartItemId('');
    //     } else {
    //       setNewCartItemId('');
    //     }
    //   }
    // });
  }, [dispatch, filterItemName, itemCategoryId, reloadItems, locationId, batchId, page]);

  // Infinite scroll: load the next page when the grid nears its bottom.
  const onItemsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!hasMore || fetchingRef.current) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 250) {
      setPage((p) => p + 1);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    let queryString = '';
    if (newCartItemId && newCartItemId != '') {
      queryString += `&parentSkuId=${newCartItemId}`;
    }

    const timerId = setTimeout(() => {
      dispatch(fetchAccountSkuByName(queryString)).unwrap().then((data: any) => {
        if (data) {
          if (data.inventories && data.inventories.length > 0) {
            const updatelist = prepareLineItems(data.inventories);
            setItems(updatelist);
            setIsLoading(false);
          } else {
            setNewCartItemId('');
            setIsLoading(false);
          }
        }
      }).catch(e => {
        setIsLoading(false);
        // console.log(e);
      });
    }, TIME_DELAY);  // 200ms delay

  }, [dispatch, isNewCart]);

  useEffect(() => {
    TotalAmount();
    if (cart.find(e => e.checked === false)) {
      setCheckAll(false);
    } else {
      setCheckAll(true);
    }
    console.log(cart);
  }, [cart])

  const prepareLineItems = (data: any, offset: number = 0) => {
    var i = offset
    return data.map((c: any) => {
      i = i + 1
      return {
        ...c,
        id: i,
        // seqNumber: i,
        // Per-batch sale rate (from the GRN, per base unit) when the card maps to a
        // batch; otherwise the SKU's sale price — read batch.price like sale.price.
        appliedRate: (c.batch && c.batch.price) ? c.batch.price : ((c.sale && c.sale.price) ? c.sale.price : 0),
        // MRP is the alternate sell rate / ceiling. Prefer the batch's per-base-unit
        // MRP (so billing at MRP matches the /unit card) over the SKU-level mrp.
        mrp: (c.batch && c.batch.unitMrp > 0) ? c.batch.unitMrp : (c.mrp || 0),
        // Batch-first purchase cost: the batch's per-base-unit cost (unitPurchaseCost,
        // exposed as batch.cost) so it lines up with the per-unit sale price and MRP;
        // falls back to the SKU's default purchase price when no batch matched
        // (services, non-batch items, or a batch with no recorded cost).
        purchasePrice: (c.batch && c.batch.cost > 0) ? c.batch.cost : (c.purchase ? c.purchase.price : '-'),
        taxPercentage: c.gst ? Number(c.gst.value) : 0,
        wholeSalePrice: c.wholeSale ? Number(c.wholeSale.price) : '-',
        // Location-scoped stock/expiry from the server when a location is filtered;
        // otherwise the global current stock.
        inStock: (c.locationStock !== undefined && c.locationStock !== null) ? c.locationStock : (c.stock && c.stock.currentStock),
        expiry: c.locationExpiry || null,
        // Stock qty on the card is in BASE units. Show the unit CODE (e.g. MLT,
        // PCS) — prefer the legacy sale-unit code, then the base unit's code, and
        // only fall back to the base unit name so it's never left blank.
        unit: (c.sale && c.sale.unit && c.sale.unit.code) ? c.sale.unit.code : ((c.baseUnit && c.baseUnit.code) ? c.baseUnit.code : (c.baseUnitName || '')),
        discount: (c.discount && c.discount.discountAmount) ? c.discount.discountAmount : 0,
        discountPercentage: (c.discount && c.discount.discountPercent) ? c.discount.discountPercent : 0,
        checked: false,
        qty: 0,
        igst: 0,
        igsttotal: 0,
        cgst: 0,
        cgsttotal: 0,
        sgst: 0,
        sgsttotal: 0,
        total: 0,
        _id: '',
        skuCode: c._id != '' ? c._id : ''
      };
    });
  };

  const onChangeCategory = (data: any) => {
    setNewCartItemId('');
    setItemCategoryValue(data);
    setItemCategoryId(data.value);
    setPage(0);
  }

  // A cart line is unique per item + batch, so two batches of the same item are
  // two separate lines.
  // Batches carry the SAME identity across locations (one Batch document, moved by
  // transfer), so the same batch can legitimately appear on two location cards.
  // Location must be part of the line identity too, or picking "batch X @ Main
  // Warehouse" and "batch X @ Bar" collapses into a single cart line.
  const sameLine = (a: any, b: any) =>
    a.skuCode === b.skuCode &&
    (a.cardBatchId || null) === (b.cardBatchId || null) &&
    (a.cardLocationId || null) === (b.cardLocationId || null) &&
    // A variant is its own cart line, distinct from the base item and from
    // sibling variants of the same SKU/batch/location.
    (a.cardVariantName || null) === (b.cardVariantName || null);

  // Add (or increment) an item in the cart — used by both the card click and the
  // "+ Add" button.
  const addToCart = (e: any) => {
    if (cart.length > 0 && cart.some((c: any) => sameLine(c, e))) {
      setCart(cart.map((c: any) => (sameLine(c, e) ? { ...c, checked: true, qty: c.qty + 1 } : c)));
    } else {
      setCart([...cart, { ...e, checked: true, qty: 1 }]);
    }
    setNewCartItemId(e.skuCode);
    setIsNewCart(Math.random().toString(36).substring(2, 7));
  };

  // Does this card have variants configured (loose-sale portions)?
  // Variants are OPTIONAL: the card itself always sells the plain item; the
  // "Variants" button opens the picker so a portion can be sold instead.
  const hasVariants = (e: any) =>
    !!(e && e.variantsEnabled && Array.isArray(e.variants) && e.variants.length > 0);

  const basisLabel = (b: string) => (b === "purchase" ? "Purchase Price" : b === "sale" ? "Sale Price" : "MRP");
  const unitLabel = (u: any) => (u && typeof u === "object" ? u.code || u.name || "" : "");

  // Resolve a variant's sale price. A percent variant multiplies the chosen basis
  // by the price of the BATCH being sold (as entered on the GRN: MRP/Sale/Purchase
  // per receiving unit), so the same SKU prices a peg differently across batches.
  // When the card carries no batch (services / non-batch / no stock), fall back to
  // the SKU master's prices.
  const resolveVariantPrice = (parent: any, v: any) => {
    const val = Number(v.salePrice) || 0;
    if (v.salePriceType === "percent") {
      const b = (parent && parent.cardBatchId && parent.batch) ? parent.batch : null;
      const skuMrp = Number(parent.mrp) || 0; // card's mrp is the SKU master mrp when no batch
      const skuSale = Number(parent.sale && parent.sale.price) || 0;
      const skuPurchase = Number(parent.purchase && parent.purchase.price) || 0;
      const base =
        v.percentBasis === "purchase" ? Number((b && b.purchasePrice) || skuPurchase) || 0 :
        v.percentBasis === "sale" ? Number((b && b.salePrice) || skuSale) || 0 :
        Number((b && b.mrp) || skuMrp) || 0;
      return Math.round((val / 100) * base * 100) / 100;
    }
    return Math.round(val * 100) / 100;
  };

  // Add (or increment) a chosen variant as its own cart line.
  // The line QUANTITY carries the variant's saved measure (e.g. 30 ml), so the
  // cart shows the ml being sold. Because the bill is rate × qty, we store the
  // resolved portion price PER-UNIT (price ÷ ml) so the line total lands back on
  // the price the user entered for that portion (₹500 chota = 16.67/ml × 30).
  const addVariantToCart = (parent: any, v: any) => {
    const price = resolveVariantPrice(parent, v);
    const portionQty = Number(v.qty) > 0 ? Number(v.qty) : 1;
    const perUnitRate = Math.round((price / portionQty) * 10000) / 10000;
    const line = {
      ...parent,
      checked: true,
      qty: portionQty,                  // the variant measure (ml) sold
      cardVariantName: v.name,          // line identity + display
      variantName: v.name,
      variantQty: v.qty,
      variantUnit: unitLabel(v.unit),
      name: `${parent.name} - ${v.name}`,
      appliedRate: perUnitRate,         // per-unit so rate × qty = portion price
      variantPortionPrice: price,       // the portion's resolved sale price
    };
    if (cart.length > 0 && cart.some((c: any) => sameLine(c, line))) {
      setCart(cart.map((c: any) => (sameLine(c, line) ? { ...c, checked: true, qty: c.qty + portionQty } : c)));
    } else {
      setCart([...cart, line]);
    }
    setNewCartItemId(parent.skuCode);
    setIsNewCart(Math.random().toString(36).substring(2, 7));
  };

  // Scan-to-cart: resolve the barcode to a SKU, then load its enriched card via the
  // SAME search pipeline the grid uses (so the cart line is built exactly like a
  // clicked one) and add it. Batch-scoped codes prefer the matching batch card.
  const onScan = async () => {
    const code = scanCode.trim();
    if (!code || scanningRef.current) return;
    // Drop a repeat of the same code within 1.2s (double Enter / scanner terminator).
    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.at < 1200) return;
    lastScanRef.current = { code, at: now };
    scanningRef.current = true;
    setScanning(true);
    setScanCode("");
    try {
      let resolved: any;
      try {
        resolved = await dispatch(resolveBarcode(code)).unwrap();
      } catch {
        toast.error(`No item is mapped to barcode "${code}"`, { toastId: `scan-${code}` });
        return;
      }
      const sku = resolved && resolved.sku;
      if (!sku || !sku._id) {
        toast.error(`No item is mapped to barcode "${code}"`, { toastId: `scan-${code}` });
        return;
      }
      // Load the item's card(s) through the existing search (filters by name).
      const filter = itemCategoryId ? `&category=${itemCategoryId}` : "";
      const locFilter = locationId ? `&locationId=${locationId}` : "";
      const batchFilter = (locationId && batchId) ? `&batchId=${batchId}` : "";
      const q = `name=${encodeURIComponent(sku.name || "")}${filter}${locFilter}${batchFilter}&page=0`;
      const data: any = await dispatch(fetchAccountSkuByName(q)).unwrap();
      const list = data && data.inventories ? prepareLineItems(data.inventories) : [];
      const wantBatch = resolved.batch && resolved.batch._id ? String(resolved.batch._id) : null;
      const match =
        (wantBatch && list.find((it: any) => String(it.skuCode) === String(sku._id) && String(it.cardBatchId) === wantBatch)) ||
        list.find((it: any) => String(it.skuCode) === String(sku._id));
      if (!match) {
        toast.error(`"${sku.name}" isn't available to add here`, { toastId: `scan-nomatch-${sku._id}` });
        return;
      }
      // Add to the cart only — leave the item grid/filters as the user left them.
      addToCart(match);
      toast.success(`Added ${sku.name}`, { toastId: `scan-ok-${sku._id}` });
    } catch {
      toast.error("Could not add the scanned item", { toastId: "scan-fail" });
    } finally {
      scanningRef.current = false;
      setScanning(false);
    }
  };

  return (
    <>
      <AddItem visible={addItem} setVisible={setAddItem} />
      <Drawer
        anchor="right"
        open={props.visible}
        onClose={(event: any, reason: any) => {
          if (reason !== 'backdropClick') {
            props.setVisible(true)
          }
        }}
        PaperProps={{ sx: { width: '90vw', maxWidth: '100%', overflow: 'hidden' } }}
      >
        <Box sx={style}>
          <Grid container pt={0}>
            <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }} >

              <Grid container >

                <Grid item xs={3.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: '-10px' }}>
                    <h3 style={{ margin: 0, marginRight: 8, whiteSpace: 'nowrap', fontSize: 18 }}>Add Item</h3>
                    <IconButton onClick={() => {
                        setFilterItemName('');
                        setItemCategoryId('');
                        setItemCategoryValue(null);
                        setLocationId('');
                        setLocationValue(null);
                        setBatchId('');
                        setBatchValue(null);
                        setBatchOptions([]);
                        setPage(0);
                        setHasMore(true);
                        setReloadItems(Math.random().toString(36).substring(2, 7));
                      }}
                      style={{ color: '#545454', backgroundColor: '#fff', border: 'none', height: 32, width: 32, flexShrink: 0 }}
                      size='small'
                    >
                      <Tooltip title="Clear filters & refresh"><RefreshRoundedIcon style={{ fontSize: 20 }} /></Tooltip>
                    </IconButton>
                    <TextField
                      placeholder='Search'
                      value={filterItemName}
                      fullWidth
                      onChange={(e) => searchItems(e.target.value.toString())}
                      className='c-text-field-3'
                      variant="standard"
                      size='small'
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: <InputAdornment position="end"><SearchOutlinedIcon style={{ fontSize: 16, marginRight: 2, paddingLeft: 0, marginLeft: -3, marginBottom: 2 }} /></InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={2.2}>
                   <Autocomplete
                    id="itemCategoryOptionsAutocomplete"
                    disablePortal
                    value={itemCategoryValue}
                    options={itemCategoryOptions && itemCategoryOptions.map(e => ({ value: e.value, label: e.label }))}
                    getOptionLabel={(option) => option.label}
                    onChange={(event, value) => onChangeCategory(value)}
                    renderOption={(props, option: { label: string }) => (
                      <li {...props}
                        style={{ fontSize: 14, height: 40 }}
                      >{option.label}</li>
                    )}
                    classes={{ paper: classes.paper }}
                    sx={{ marginTop: '-10px', width: '100%' }}
                    renderInput={(params) => <TextField {...params}
                      placeholder="Select Item Category"
                      required
                      className='c-text-field-3'
                      variant="standard"
                      size='small'
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />}
                  />
                </Grid>

                <Grid item xs={2.2}>
                  <Autocomplete
                    id="locationFilterAutocomplete"
                    disablePortal
                    value={locationValue}
                    options={locationOptions}
                    getOptionLabel={(option: any) => option.label}
                    onChange={(event, value) => onChangeLocation(value)}
                    renderOption={(props, option: { label: string }) => (
                      <li {...props} style={{ fontSize: 14, height: 40 }}>{option.label}</li>
                    )}
                    classes={{ paper: classes.paper }}
                    sx={{ marginTop: '-10px', width: '100%' }}
                    renderInput={(params) => <TextField {...params}
                      placeholder="Filter by location"
                      className='c-text-field-3'
                      variant="standard"
                      size='small'
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />}
                  />
                </Grid>

                <Grid item xs={2.2}>
                  <Autocomplete
                    id="batchFilterAutocomplete"
                    disablePortal
                    disabled={!locationId}
                    value={batchValue}
                    options={batchOptionList}
                    getOptionLabel={(option: any) => option.label}
                    onChange={(event, value) => onChangeBatch(value)}
                    renderOption={(props, option: { label: string }) => (
                      <li {...props} style={{ fontSize: 14, height: 40 }}>{option.label}</li>
                    )}
                    classes={{ paper: classes.paper }}
                    sx={{ marginTop: '-10px', width: '100%' }}
                    renderInput={(params) => <TextField {...params}
                      placeholder={locationId ? "Filter by batch" : "Select location first"}
                      className='c-text-field-3'
                      variant="standard"
                      size='small'
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />}
                  />
                </Grid>

                {/* <Grid item xs={3.3}>
                  <Button size='small' className='c-dashed-button' variant='outlined' sx={{ marginTop:-0.7,borderStyle:'dashed',borderRadius:'5px',width:'95%'}}
                      onClick={()=> setAddItem(true)}>Create New Item</Button>
                </Grid> */}

                <div style={{ position: 'absolute', right: 7, top: 7 }}>
                  {/* <IconButton size='small' onClick={() => setReloadItems(Math.random().toString(36).substring(2, 7))} >
                    <ReplayOutlinedIcon color="primary" />
                  </IconButton> */}
                  {/* <IconButton size='small'
                   onClick={() => onSubmit()} >
                    <CheckCircleOutlineOutlinedIcon color="primary" />
                  </IconButton> */}
                  <Button
                    className={'buttonContainedRoot'}
                    size='small'
                    onClick={() => onSubmit()}
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginRight: 10 }}
                    variant="contained"
                  >
                    Continue
                  </Button>

                  <Button
                    className={'buttonContainedRoot'}
                    size='small'
                    onClick={() => onClose()}
                    style={{ color: '#c33b3b', backgroundColor: '#fff', border: '0.5px solid #c33b3b', height: 35, borderRadius: 7 }}
                    variant="contained"
                  >
                    Cancel
                  </Button>
                  {/* <Button variant="outlined" color="primary" style={{borderWidth:2.5,borderRadius:25,height:25,fontSize:12,paddingTop:6,paddingLeft:'0px',paddingRight:'0px',marginTop:1,width:'auto',minWidth:'50px'}}>Add</Button> */}
                  {/* <IconButton size='small' onClick={() => onClose()} style={{ marginRight: -5 }} >
                    <HighlightOffOutlinedIcon color="error" />
                  </IconButton> */}
                </div>
              </Grid>
            </Grid>

            {/* {JSON.stringify(items)} */}

          </Grid>
          <Grid container style={{ height: '100%' }} >

            <Grid item xs={7.5} onScroll={onItemsScroll} style={{ width: '65%', position: 'absolute', top: 50, bottom: 0, overflow: 'auto', borderRight: '1px solid #e9ecf1' }}>
              <div style={{ position: 'sticky', top: 0, zIndex: 3, background: '#fff', padding: '10px 16px', borderBottom: '1px solid #e9ecf1', display: 'flex', gap: 8, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Scan a barcode (or type the code) and press Enter to add"
                  value={scanCode}
                  onChange={(e) => setScanCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onScan(); } }}
                  className="c-text-field-3"
                  variant="standard"
                  size="small"
                  disabled={scanning}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: <InputAdornment position="start"><QrCodeScannerIcon style={{ fontSize: 18, color: '#6b46c1', marginRight: 4 }} /></InputAdornment>,
                  }}
                />
                <Button variant="outlined" size="small" onClick={onScan} disabled={scanning || !scanCode.trim()}
                  style={{ color: '#6b46c1', borderColor: '#6b46c1', whiteSpace: 'nowrap', height: 32 }}>
                  Add
                </Button>
              </div>
              <Grid container p={2} spacing={2}>

                {items.length <= 0 ? <div style={{ height: '100%', width: '100%', marginTop: '30%', textAlign: 'center' }}><h2 style={{ color: '#9da7b7' }}>Please Add Item</h2></div> :
                  <>
                    {(newCartItemId && newCartItemId != '') &&
                      <Grid item xs={12}>
                        <Typography style={{ fontSize: 16, fontWeight: 500 }}>
                          Would you like to add sub items to the cart ?
                          <Button
                            className={'buttonContainedRoot'}
                            size='small'
                            onClick={() => setReloadItems(Math.random().toString(36).substring(2, 7))}
                            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginLeft: 10 }}
                            // style={{ color: '#c33b3b', backgroundColor: '#fff',border:'0.5px solid #c33b3b',height:35,borderRadius:7,marginLeft:10}}
                            variant="contained"
                          >
                            No I Don't. Load All Items...
                          </Button>

                        </Typography>
                      </Grid>
                    }
                    {isLoading && page === 0 ?
                      <>
                        {Array.from(Array(18).keys()).map((e: any) =>
                          <Grid item xs={4}>
                            <div className={'c-card2'} style={{ padding: 10, paddingLeft: 10, border: '' }}>

                              <Grid container>
                                <Grid item xs={3.5}>
                                  <Skeleton animation="wave" variant="circular" width={30} height={30} />
                                </Grid>
                                <Grid item xs={8}>
                                  <Skeleton animation="wave" height={20} />
                                  <Skeleton animation="wave" height={20} />
                                </Grid>
                              </Grid>
                              <Skeleton animation="wave" height={20} />

                            </div>
                          </Grid>
                        )}
                      </>
                      : displayedItems.map((e: any) =>
                        <Grid key={e.id} item xs={4}>
                          <div className={'c-card2'} onClick={() => addToCart(e)} style={{ position: 'relative', cursor: 'pointer', padding: 10, paddingLeft: 10, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', border: cart.filter(o => o.id === e.id).length ? '1px solid #488dff' : '' }}>
                            {e.itemType && (
                              <span
                                title={e.itemType}
                                style={{ position: 'absolute', top: 4, left: 4, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9, backgroundColor: e.itemType === 'Service' ? '#39a85b' : e.itemType === 'Raw Material' ? '#e08a2b' : '#488dff', color: '#fff', fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, border: '1.5px solid #fff' }}
                              >
                                {e.itemType === 'Raw Material' ? 'RW' : String(e.itemType).charAt(0).toUpperCase()}
                              </span>
                            )}
                            <Grid container>
                              <Grid item xs={3.5}>
                                <img src={DefaultImg} style={{ objectFit: 'contain', padding: 10, width: 50, height: 50, borderRadius: 7, border: '.5px solid #e9ecf1' }} />
                              </Grid>
                              <Grid item xs={8}>
                                <Tooltip title={<><div>{e.name}</div>{e.description && <div style={{ marginTop: 4, opacity: 0.85 }}>{e.description}</div>}</>} placement="top-start">
                                  <p style={{ fontSize: 13, minHeight: 40, margin: 0, wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {String(e.name)}
                                  </p>
                                </Tooltip>
                              </Grid>
                            </Grid>
                            {e.expiry && (
                              <div style={{ marginBottom: 6 }}>
                                <span style={{ display: 'inline-block', backgroundColor: '#fbecec', color: '#c33b3b', borderRadius: 6, padding: '1px 6px', fontSize: 11, fontWeight: 600 }}>Exp: {Utils.formatDateLong(e.expiry)}</span>
                              </div>
                            )}
                            {(e.cardBatchNumber || (!locationId && e.cardLocationName)) && (
                              <div style={{ fontSize: 11, color: '#5b6472', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                                {e.cardBatchNumber && <span style={{ backgroundColor: '#eef1f6', borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap' }}>Batch {e.cardBatchNumber}</span>}
                                {!locationId && e.cardLocationName && <span style={{ color: '#7a8290', display: 'inline-flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}><LocationOnOutlinedIcon style={{ fontSize: 13, flexShrink: 0 }} />{e.cardLocationName}</span>}
                              </div>
                            )}
                            <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingTop: 6 }}>
                              <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 14, margin: 0, whiteSpace: 'nowrap' }}>{(e.appliedRate && e.appliedRate > 0) ? ('₹ ' + (Math.round(e.appliedRate * 100) / 100) + (e.unit ? ' / ' + e.unit : '')) : ((e.mrp && e.mrp > 0) ? '₹ ' + (Math.round(e.mrp * 100) / 100) : '')}</h5>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span style={{ fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>{e.inStock} <span style={{ color: '#9da7b7' }}>{e.unit}</span></span>
                                <IconButton
                                  onClick={(ev) => { ev.stopPropagation(); hasVariants(e) ? setVariantParent(e) : addToCart(e); }}
                                  style={{ flexShrink: 0, fontSize: 14, color: '#488dff', backgroundColor: '#f1f0fc', border: '1px solid #488dff', height: 25, borderRadius: 8 }}
                                  size='small' ><AddRoundedIcon style={{ fontSize: 16 }} /> {hasVariants(e) ? 'Variants' : 'Add'}</IconButton>
                              </div>
                            </div>

                          </div>
                        </Grid>)}</>}

                {isLoading && page > 0 && (
                  <Grid item xs={12} style={{ textAlign: 'center', padding: 10, color: '#9da7b7', fontSize: 13 }}>
                    Loading more…
                  </Grid>
                )}

                {/* {items.length <= 0 ? <div style={{height:'100%',width:'100%',marginTop:'30%',textAlign:'center'}}><h2 style={{color:'#9da7b7'}}>Please Add Item</h2></div> :
                <>{items.map((e: any) =>
                  <Grid key={e.id} item xs={2.4}>
                    <div className={'c-card2'} style={{ padding: 10, paddingLeft: 10, border: cart.filter(o=> o.id === e.id).length ? '1px solid #488dff' : '' }}>
                      <IconButton
                        onClick={() => {
                          // const updatelist = items.map((c: any) => {
                          //   if (c.id === e.id) {
                          //     return { ...c, checked: true, qty: e.qty + 1 };
                          //   } else {
                          //     return c;
                          //   }
                          // });
                          // let s = { ...items };
                          // s = updatelist;
                          // setItems(s);
                          if(cart.length > 0) {
                            const updatelist = cart.map((c: any) => {
                              if (c.id === e.id) {
                                return { ...c, checked: true, qty: c.qty + 1 };
                              } else {
                                return c;
                              }
                            });

                            if(updatelist.length > 0) {
                              console.log(updatelist[0].id, e.id);
                              if(updatelist.filter(o=> o.id === e.id).length > 0) {
                                let s = { ...cart };
                                s = updatelist;
                                setCart(s);
                              } else {
                                setCart([...cart, { ...e, checked: true, qty: 1 }]);
                              }
                            } else {
                              setCart([...cart, { ...e, checked: true, qty: 1 }]);
                            }
                          } else {
                            setCart([...cart, { ...e, checked: true, qty: 1 }]);
                          }
                        }}
                        style={{ float: 'right', marginTop: -18, marginRight: -18, color: '#488dff', backgroundColor: '#f1f0fc', border: '1px solid #488dff', height: 30, width: 30 }}
                        size='small' ><AddRoundedIcon style={{ fontSize: 20 }} /></IconButton>

                      <img src={DefaultImg} style={{ marginTop: -15, objectFit: 'contain', padding: 10, width: '100%', borderRadius: 7, border: '.5px solid #e9ecf1', height: 100 }} />
                      <p style={{ fontSize: 13, height: 60 }}>{String(e.name).length > 55 ? String(e.name).substring(0, 55) + '...' : String(e.name)}</p>
                      <span style={{ float: 'right', fontSize: 12, marginTop: 2.5 }}>{e.inStock} <span style={{ color: '#9da7b7' }}>{e.unit}</span></span>
                      <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 14 }}>{(e.appliedRate && e.appliedRate > 0) && '₹ ' + e.appliedRate}</h5>
                    </div>
                  </Grid>)}</>} */}


              </Grid>
            </Grid>
            <Grid item xs={4.5} pb={5} style={{ width: '36%', position: 'absolute', top: 50, bottom: 0, right: 5, overflow: 'auto' }}>

              {cart.filter(e => e.checked === true).length <= 0 ? <h2 style={{ color: '#9da7b7', textAlign: 'center', marginTop: '50%' }}>Please Select Item</h2> :

                <>{cart.filter(e => e.checked === true).map((e: any, index: any) =>

                  <Grid key={e.id} container style={{ borderBottom: '1px solid #e9ecf1' }} pt={1} pb={1}>

                    <Grid item xs={8} mb={1}>
                      <h5 style={{ fontWeight: '700', color: '#000', fontSize: 14 }}>{e.name}</h5>
                      {(e.cardBatchNumber || e.cardLocationName || e.expiry) && (
                        <span style={{ fontSize: 11, color: '#7a8290' }}>
                          {[
                            e.cardBatchNumber ? `Batch ${e.cardBatchNumber}` : "",
                            e.cardLocationName || "",
                            e.expiry ? `Exp:${Utils.formatDateLong(e.expiry)}` : "",
                          ].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton
                        onClick={() => {
                          setCart(cart.filter(o => !sameLine(o, e)));
                        }}
                        style={{ float: 'right', marginTop: 0, marginRight: 10, color: '#c33b3b', backgroundColor: '#fbecec', border: '1px solid #c33b3b', height: 25, width: 25 }}
                        size='small' ><CloseRoundedIcon style={{ fontSize: 16 }} /></IconButton>
                      {/* Price chip hidden — Sale Price & Purchase Price are already shown below.
                      {(e.appliedRate && e.appliedRate > 0) &&
                        <>
                          <Tooltip title={`Sale Price`}>
                            <Chip size='small' style={{ float: 'right', marginRight: 8, borderRadius: 7, backgroundColor: '#e5e5f9', fontWeight: 700 }} label={`${Utils.formatNumber(e.appliedRate)}`} />
                          </Tooltip>
                        </>
                      } */}
                    </Grid>
                    {/* Three prices packed into one row (8 cols) so the qty stepper
                        stays beside them instead of wrapping to the next row. */}
                    <Grid item xs={8}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 11, margin: 0, whiteSpace: 'nowrap' }}>MRP</h5>
                          <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 13, margin: '2px 0 3px', whiteSpace: 'nowrap' }}>{(e.mrp && e.mrp > 0) ? Utils.formatNumber(Math.round(e.mrp * 100) / 100) : '-'}</h5>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 11, margin: 0, whiteSpace: 'nowrap' }}>SALE PRICE</h5>
                          <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 13, margin: '2px 0 3px', whiteSpace: 'nowrap' }}>{(e.appliedRate && e.appliedRate > 0) ? Utils.formatNumber(Math.round(e.appliedRate * 100) / 100) : '-'}</h5>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 11, margin: 0, whiteSpace: 'nowrap' }}>PURCHASE PRICE</h5>
                          <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 13, margin: '2px 0 3px', whiteSpace: 'nowrap' }}>{(e.purchasePrice && e.purchasePrice > 0) ? Utils.formatNumber(Math.round(e.purchasePrice * 100) / 100) : '-'}</h5>
                        </div>
                      </div>
                    </Grid>


                    <Grid item xs={4} pt={1} pl={1}>
                      <InputNumber
                        steps={1}
                        myvalue={e.checked === false ? 0 : e.qty}
                        // min={1} max={999} value={e.checked === true ? e.qty : 0}
                        onChange={(val) => {
                          if (val && val) {
                            if (val <= 0) {
                              setCart(cart.filter(o => !sameLine(o, e)));
                            }
                            else {
                              const updatelist = cart.map((c: any) => {
                                if (sameLine(c, e)) {
                                  return { ...c, checked: true, qty: val };
                                } else {
                                  return c;
                                }
                              });
                              let s = { ...cart };
                              s = updatelist;
                              setCart(s);
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {(e.wholeSalePrice && e.wholeSalePrice > 0) ? <Tooltip title="Wholesale Price"><Chip size='small' style={{ borderRadius: 7, color: '#49a563', backgroundColor: '#edf6ef', marginRight: 10 }} label={<>{'₹ ' + e.wholeSalePrice}</>} /></Tooltip> : ''}
                      {(e.discount && e.discount > 0) ? <Tooltip title="Discount"><Chip size='small' style={{ borderRadius: 7, color: '#488dff', backgroundColor: '#f1f0fc' }} label={<>Discount : {'₹ ' + e.discount} {e.discountPercentage > 0 ? `(${e.discountPercentage}%)` : ''}</>} /></Tooltip> : ''}
                      {e.inStock ? <Tooltip title="In Stock"><Chip size='small' style={{ float: 'right', marginRight: 20, borderRadius: 7, backgroundColor: '#fafafb' }} label={<span>{e.inStock} <span style={{ color: '#9da7b7' }}>{e.unit}</span></span>} /></Tooltip> : ''}
                    </Grid>
                  </Grid>)}</>}


              <div style={{ height: 40, position: 'fixed', width: '36%', bottom: 0, backgroundColor: '#fff' }}>
                {cart.filter(e => e.checked === true).length === 0 ? '' : <>
                  <Divider />
                  <Grid container spacing={0} p={0.5}>
                    <Grid item xs={5} >
                      <h4 style={{ fontSize: 14, fontWeight: 400, color: '#488dff' }}>{cart.filter(e => e.checked === true).length} Items Selected </h4>
                    </Grid>
                    <Grid item xs={7} >
                      <h4 style={{ textAlign: 'right', fontSize: 16, marginRight: 60, fontWeight: 400 }}>Total Amount : <b>{Utils.formatNumber(totalAmount, true, true)}</b> </h4>
                    </Grid>
                  </Grid></>}
              </div>

            </Grid>
          </Grid>

          {/* Variant picker — slides out from the border between the item list and
              the cart. Shows the item's variants as cards in a single column. */}
          {variantParent && (
            <>
              {/* backdrop over the item-list area, up to the cart's left edge (click to dismiss).
                  The cart panel is `right: 5px; width: 36%`, so its left edge is at calc(36% + 5px). */}
              <Box
                onClick={() => setVariantParent(null)}
                sx={{ position: 'absolute', top: 50, left: 0, bottom: 0, right: 'calc(36% + 15px)', backgroundColor: 'rgba(0,0,0,0.12)', zIndex: 5 }}
              />
              <Box
                sx={{
                  // Right edge sits on the divider (cart's left edge). A visible right
                  // border draws the "middle border" line separating the drawer from
                  // the cart (both are white, so without it they blend together). The
                  // drawer opens LEFT over the item list, so its shadow is cast left.
                  position: 'absolute', top: 50, bottom: 0, right: 'calc(36% + 15px)', width: 360, maxWidth: '55%',
                  backgroundColor: '#fff', borderRight: '2px solid #c1c5cb', boxShadow: '-10px 0 24px rgba(0,0,0,0.12)',
                  zIndex: 6, display: 'flex', flexDirection: 'column',
                  animation: 'variantSlideIn 220ms ease',
                  '@keyframes variantSlideIn': { from: { transform: 'translateX(24px)', opacity: 0 }, to: { transform: 'translateX(0)', opacity: 1 } },
                }}
              >
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e9ecf1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#9da7b7' }}>Select a variant</div>
                    <div style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{variantParent.name}</div>
                  </div>
                  <IconButton size='small' onClick={() => setVariantParent(null)}
                    style={{ flexShrink: 0, color: '#c33b3b', backgroundColor: '#fbecec', border: '1px solid #c33b3b', height: 28, width: 28 }}>
                    <CloseRoundedIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </div>
                <div style={{ overflow: 'auto', padding: 12, flex: 1 }}>
                  {variantParent.variants.map((v: any, vi: number) => {
                    const price = resolveVariantPrice(variantParent, v);
                    const inCart = cart.filter((c: any) => c.id === variantParent.id && c.cardVariantName === v.name).length;
                    return (
                      <div key={vi} className={'c-card2'} onClick={() => addVariantToCart(variantParent, v)}
                        style={{ cursor: 'pointer', padding: 10, marginBottom: 10, border: inCart ? '1px solid #488dff' : '' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word' }}>{v.name}</div>
                            <div style={{ fontSize: 12, color: '#7a8290', marginTop: 2 }}>
                              Qty: {v.qty}{unitLabel(v.unit) ? ' ' + unitLabel(v.unit) : ''}
                              {' · '}
                              {v.salePriceType === 'percent' ? `${v.salePrice}% of ${basisLabel(v.percentBasis)}` : 'Flat'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <h5 style={{ margin: 0, fontSize: 14, whiteSpace: 'nowrap' }}>₹ {price}</h5>
                            <IconButton
                              onClick={(ev) => { ev.stopPropagation(); addVariantToCart(variantParent, v); }}
                              style={{ fontSize: 14, color: '#488dff', backgroundColor: '#f1f0fc', border: '1px solid #488dff', height: 25, borderRadius: 8 }}
                              size='small'><AddRoundedIcon style={{ fontSize: 16 }} /> Add</IconButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Box>
            </>
          )}

          {/* <div style={{height:200, overflowX:'auto',overflowY:'auto',marginBottom:7}}>
          <Table stickyHeader sx={{ minWidth: 700}} size='small' aria-label="customized table">
          <TableHead>
              <TableRow>
                  <StyledTableCell align="left" width={5}>
                      <Checkbox
                      sx={{
                          '&:hover': { bgcolor: 'transparent' },
                          ml:-1
                      }}
                      checked={checkAll}
                      onChange={()=>{
                        setTotalAmount(0);
                        setCheckAll(!checkAll);
                        const updatelist = items.map((c:any) => {
                              return {...c, checked: !checkAll, qty:checkAll === true ? 0 : c.qty > 0 ? c.qty : 1};
                          });
                          let s = {...items};
                          s = updatelist;
                          setItems(s);
                      }}
                      disableRipple
                      checkedIcon={<BpCheckedIcon />}
                      icon={<BpIcon />}
                  /></StyledTableCell>
                  <StyledTableCell width={200}>ITEM NAME </StyledTableCell> 
                  <StyledTableCell>HSN CODE</StyledTableCell>
                  <StyledTableCell>MRP</StyledTableCell>
                  <StyledTableCell>PURCHASE PRICE</StyledTableCell>
                  <StyledTableCell>SALE PRICE</StyledTableCell>
                  <StyledTableCell>WHOLESALE RATE</StyledTableCell>
                  <StyledTableCell>Discount</StyledTableCell>
                  <StyledTableCell>IN STOCK</StyledTableCell>
                  <StyledTableCell align="left" width={100}>QUANTITY</StyledTableCell>
              </TableRow>
          </TableHead>
          <TableBody>
            
              {items.map((e:any) => 
              <StyledTableRow key={e.id}>
                  <StyledTableCell align="left" >
                      <Checkbox
                      sx={{
                          '&:hover': { bgcolor: 'transparent' },
                          ml:-1
                      }}
                      checked={e.checked || e.qty>0}
                      onChange={()=>{
                          const updatelist = items.map((c:any) => {
                              if (c.id === e.id) {
                                console.log(c.id)
                                console.log(e.id)
                                return {...c, checked: !c.checked, qty:c.checked === true ? 0 : 1};
                              } else {
                                return c;
                              }
                            });
                            let s = {...items};
                            s = updatelist;
                            setItems(s);
                      }}
                      // onClick={()=> setShip(!ship)}
                      disableRipple
                      checkedIcon={<BpCheckedIcon/>}
                      icon={<BpIcon />}
                  /></StyledTableCell>
                  <StyledTableCell align="left">{e.name}</StyledTableCell>
                  <StyledTableCell align="left">{(e.hsnCode && e.hsnCode != '') ? e.hsnCode : '-'}</StyledTableCell>
                  <StyledTableCell align="left">{(e.mrp && e.mrp > 0) && '₹ '+ e.mrp}</StyledTableCell>
                  <StyledTableCell align="left">{(e.purchasePrice && e.purchasePrice > 0) && '₹ '+ e.purchasePrice}</StyledTableCell>
                  <StyledTableCell align="left">{(e.appliedRate && e.appliedRate > 0) && '₹ '+ e.appliedRate}</StyledTableCell>
                  <StyledTableCell align="left">{(e.wholeSalePrice && e.wholeSalePrice > 0) && '₹ '+e.wholeSalePrice}</StyledTableCell>
                  <StyledTableCell align="left">{(e.discount && e.discount > 0) && '₹ '+ e.discount} {e.discountPercentage > 0 ? `(${e.discountPercentage}%)` : ''}</StyledTableCell>
                  <StyledTableCell align="left">{e.inStock} {e.unit}</StyledTableCell>
                  <StyledTableCell align="right">
                      <div style={{float:'right',height:18,marginRight:10}}>
                          <InputNumber 
                          steps={1}
                          myvalue={e.checked === false ? 0 : e.qty}
                          // min={1} max={999} value={e.checked === true ? e.qty : 0}
                          onChange={(val)=> {
                              if(val && val) {
                              const updatelist = items.map((c:any) => {
                                  if (c.id === e.id) {
                                    return {...c, checked: val <= 0 ? false : true, qty: val};
                                  } else {
                                    return c;
                                  }
                                });
                                let s = {...items};
                                s = updatelist;
                                setItems(s);
                              } 
                          }}/>
                      </div>
                  </StyledTableCell>
              </StyledTableRow>)}
              
              
          </TableBody>
          </Table>
          </div> */}

        </Box>
      </Drawer>
    </>
  )
};
export default ItemModel;